<script setup lang="ts">
import { Radio as SatelliteDish } from 'lucide-vue-next'

/** Full-bleed overlay + VoIP-themed loader (session logout / leave impersonation). */
</script>

<template>
  <div
    class="absolute inset-0 flex items-center justify-center bg-foreground/20 backdrop-blur-md dark:bg-background/60 dark:backdrop-blur-lg"
    aria-hidden="true"
  >
    <div class="voip-session-loader">
      <svg
        class="voip-session-loader__svg"
        viewBox="0 0 80 80"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient
            id="voip-session-stroke"
            x1="0"
            y1="0"
            x2="80"
            y2="80"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stop-color="currentColor" stop-opacity="0.15" />
            <stop offset="45%" stop-color="currentColor" stop-opacity="1" />
            <stop offset="100%" stop-color="currentColor" stop-opacity="0.15" />
          </linearGradient>
        </defs>

        <circle
          class="voip-session-loader__track"
          cx="40"
          cy="40"
          r="32"
          stroke="currentColor"
          stroke-width="1"
        />
        <circle
          class="voip-session-loader__track voip-session-loader__track--inner"
          cx="40"
          cy="40"
          r="22"
          stroke="currentColor"
          stroke-width="1"
        />

        <circle
          class="voip-session-loader__signal voip-session-loader__signal--1"
          cx="40"
          cy="40"
          r="14"
          stroke="currentColor"
          stroke-width="1"
        />
        <circle
          class="voip-session-loader__signal voip-session-loader__signal--2"
          cx="40"
          cy="40"
          r="14"
          stroke="currentColor"
          stroke-width="1"
        />

        <circle
          class="voip-session-loader__ring voip-session-loader__ring--outer"
          cx="40"
          cy="40"
          r="32"
          stroke="url(#voip-session-stroke)"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-dasharray="52 149"
        />
        <circle
          class="voip-session-loader__ring voip-session-loader__ring--inner"
          cx="40"
          cy="40"
          r="22"
          stroke="url(#voip-session-stroke)"
          stroke-width="2"
          stroke-linecap="round"
          stroke-dasharray="38 100"
        />

        <g class="voip-session-loader__satellites">
          <circle cx="40" cy="10" r="2" fill="currentColor" />
          <circle cx="62" cy="52" r="1.5" fill="currentColor" opacity="0.7" />
          <circle cx="18" cy="52" r="1.5" fill="currentColor" opacity="0.7" />
        </g>
      </svg>

      <SatelliteDish class="voip-session-loader__icon" :stroke-width="1.75" aria-hidden="true" />
    </div>
  </div>
</template>

<style scoped>
.voip-session-loader {
  position: relative;
  color: hsl(var(--primary));
  width: 5rem;
  height: 5rem;
}

.voip-session-loader__svg {
  width: 100%;
  height: 100%;
}

.voip-session-loader__icon {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 1.625rem;
  height: 1.625rem;
  transform: translate(-50%, -50%);
  color: hsl(var(--primary));
  animation: voip-icon-pulse 2.4s ease-in-out infinite;
}

.voip-session-loader__track {
  opacity: 0.18;
}

.voip-session-loader__track--inner {
  opacity: 0.12;
}

.voip-session-loader__signal {
  transform-origin: 40px 40px;
  opacity: 0;
  fill: none;
}

.voip-session-loader__signal--1 {
  animation: voip-signal-burst 2.4s ease-out infinite;
}

.voip-session-loader__signal--2 {
  animation: voip-signal-burst 2.4s ease-out infinite 1.2s;
}

.voip-session-loader__ring {
  transform-origin: 40px 40px;
}

.voip-session-loader__ring--outer {
  animation: voip-spin 1.6s cubic-bezier(0.5, 0.1, 0.3, 1) infinite;
}

.voip-session-loader__ring--inner {
  animation: voip-spin-reverse 1.15s cubic-bezier(0.5, 0.1, 0.3, 1) infinite;
}

.voip-session-loader__satellites {
  transform-origin: 40px 40px;
  animation: voip-spin 4.5s linear infinite;
}

@keyframes voip-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes voip-spin-reverse {
  to {
    transform: rotate(-360deg);
  }
}

@keyframes voip-signal-burst {
  0% {
    transform: scale(0.55);
    opacity: 0.55;
  }
  70%,
  100% {
    transform: scale(2.35);
    opacity: 0;
  }
}

@keyframes voip-icon-pulse {
  0%,
  100% {
    opacity: 0.88;
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.06);
  }
}
</style>
